import type { ButtonHTMLAttributes } from "react";
import React, { useState } from "react";
import styled from "styled-components";

import { useIntl } from "../../../../shared/core/i18n/use-intl";
import { useRTL } from "../../../domain/language/use-rtl";
import { StaticAssets } from "../../styles/custom-assets";
import { theme } from "../../styles/theme";
import { UIConstants } from "../../styles/uiConstants";
import { DefaultButton } from "../buttons/default-button";
import { AccountIcon } from "../svg/account-icon";
import { BigBankToWalletIcon } from "../svg/big-bank-to-wallet-icon";
import { DocumentIcon } from "../svg/document-icon";
import { LoanIcon } from "../svg/loan-icon";
import { ServiceIcon } from "../svg/service-icon";
import { TransferIcon } from "../svg/transfer-icon";
import { sideBarColors } from "./component/colors";
import type { NavType } from "./nav-type";

interface NavButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  isActive?: boolean;
  linkType: NavType;
  withBackground?: boolean;
}

/** The new menu artwork ships as flat white SVG files, so it is rendered as an image. */
const MenuIconImage = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
`;

const NavLink = {
  account: { icon: <MenuIconImage src={StaticAssets.menuHouse} alt="" />, labelKey: "navigationMenu.accountLink" },
  transfers: { icon: <TransferIcon />, labelKey: "navigationMenu.transferLink" },
  services: { icon: <ServiceIcon />, labelKey: "navigationMenu.payBillLink" },
  cards: { icon: <MenuIconImage src={StaticAssets.menuCard} alt="" />, labelKey: "navigationMenu.cardsLink" },
  savings: { icon: <BigBankToWalletIcon />, labelKey: "navigationMenu.savingsLink" },
  settings: { icon: <AccountIcon />, labelKey: "navigationMenu.settingsLink" },
  profile: { icon: <MenuIconImage src={StaticAssets.menuProfile} alt="" />, labelKey: "navigationMenu.profileLink" },
  loan: { icon: <LoanIcon />, labelKey: "navigationMenu.loansLink" },
  mandates: { icon: <DocumentIcon />, labelKey: "navigationMenu.mandatesLink" },
};

export function NavButton(props: NavButtonProps) {
  const { linkType, isActive, withBackground = true, onClick, ...rest } = props;
  const { formatMessage } = useIntl();
  const { isRTL } = useRTL();

  const [toolTipVisible, setToolTipVisible] = useState(false);

  return (
    <ButtonBackground $active={isActive} $withoutBackground={!withBackground}>
      <Button
        onMouseEnter={() => setToolTipVisible(true)}
        onMouseLeave={() => setToolTipVisible(false)}
        role="menuitem"
        $active={isActive}
        onClick={(e) => {
          setToolTipVisible(false);
          onClick?.(e);
        }}
        $isRTL={isRTL}
        {...rest}
      >
        {NavLink[linkType].icon}
        <BaseLabel $active={isActive} $isRTL={isRTL}>
          {formatMessage(NavLink[linkType].labelKey)}
        </BaseLabel>
      </Button>
      <Tooltip $isVisible={toolTipVisible} $isRTL={isRTL}>
        {formatMessage(NavLink[linkType].labelKey)}
      </Tooltip>
    </ButtonBackground>
  );
}

interface SubNavButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  isActive?: boolean;
  name: string;
}

export function SubNavButton(props: SubNavButtonProps) {
  const { isActive, name, onClick, ...rest } = props;
  const { formatMessage } = useIntl();
  const { isRTL } = useRTL();

  return (
    <SubButtonBackground $active={isActive}>
      <Button role="menuitem" $active={isActive} onClick={onClick} $isRTL={isRTL} {...rest}>
        <SubItemLabel $active={isActive} $isRTL={isRTL}>
          {formatMessage(name)}
        </SubItemLabel>
      </Button>
    </SubButtonBackground>
  );
}

const ButtonBackground = styled.div<{ $active?: boolean; $withoutBackground?: boolean }>`
  display: flex;
  flex-direction: row;
  background-color: ${({ $active, $withoutBackground }) =>
    $active && !$withoutBackground ? sideBarColors.selectedBackground : sideBarColors.background};
  margin: 0 16px;
  padding: 12px;
  border-radius: 13px;
  flex: 1;
`;

const SubButtonBackground = styled(ButtonBackground)`
  margin: 0 16px 0 48px;
  justify-content: flex-start;
`;

const Button = styled(DefaultButton)<{ $active?: boolean; $isRTL: boolean }>`
  position: relative;
  align-items: flex-start;
  justify-content: flex-start;

  svg {
    width: 24px;
    height: 24px;
    fill: ${({ $active }) => ($active ? sideBarColors.activeItem : sideBarColors.inactiveItem)};
  }
  &:disabled {
    opacity: 0.3;
  }
`;

const BaseLabel = styled.span<{ $active?: boolean; $isRTL: boolean }>`
  font-size: 0.9375rem;
  ${theme.text};
  color: ${({ $active }) => ($active ? sideBarColors.activeItem : sideBarColors.inactiveItem)};
  margin-left: ${(props) => (props.$isRTL ? 0 : 12)}px;
  margin-right: ${(props) => (props.$isRTL ? 12 : 0)}px;

  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 170px;
`;

const SubItemLabel = styled(BaseLabel)`
  margin-left: ${(props) => (props.$isRTL ? 0 : 4)}px;
  margin-right: ${(props) => (props.$isRTL ? 4 : 0)}px;
  max-width: 100%;
  @media (max-width: ${UIConstants.DESKTOP_SMALL_BREAKPOINT - 1}px) {
    font-size: 0.875rem;
  }
`;

const Tooltip = styled.span<{ $isVisible: boolean; $isRTL: boolean }>`
  ${theme.text}
  text-align: ${(props) => (props.$isRTL ? "right" : "left")};
  color: ${sideBarColors.toolTipText};
  font-size: 0.825rem;
  height: 23px;
  min-width: fit-content;
  width: intrinsic; /* Safari/WebKit use a non-standard name */
  width: -moz-max-content; /* Firefox/Gecko */
  width: -webkit-max-content; /* Chrome */

  padding: 3px 8px;
  border-radius: 8px;
  background-color: ${sideBarColors.toolTipBackground};
  position: absolute;
  margin-right: ${(props) => (props.$isRTL ? 60 : 0)};
  margin-left: ${(props) => (props.$isRTL ? 0 : 60)};
  opacity: ${(props) => (props.$isVisible ? 1 : 0)};
  z-index: 10;
  transition: opacity 0.5s;
  display: none;
`;
